import React, { Component } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { Link } from 'react-router-dom';
import { Grid } from 'react-bootstrap';
import Select from 'react-select';
import moment from 'moment';
import memoize from 'memoize-one';
import fastMemoize from 'fast-memoize';
import ButtonStandardPrimary from 'components/atoms/buttons/standard/Primary';
import { LoadingTile } from 'components/molecules/ErrorTile';
import { fetchList } from 'services/entities/duck';
import { ENTITY_PLATFORMS } from 'constants/entityPlatforms';
import DebounceInputChange from 'hoc/DebounceInputChange';
import { debounce, get } from 'lodash';
import StyledReactTable from 'components/molecules/StyledReactTable';
import Pagination from 'components/molecules/tables/components/Pagination';
import CellDiv from 'components/molecules/tables/components/CellDiv';
import TrackedComponentEnhancer from 'hoc/TrackedComponent';

const TrackedCloneLink = TrackedComponentEnhancer({
    trackedName: 'Clone-Entity-From-Table-View',
    targetComponent: Link,
    sendToMixPanel: true
});

const NOT_AVAILABLE = 'N/A';
const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 25;

const DebouncedInput = DebounceInputChange(500)(props => <input {...props} />);

const generateFilterValue = (filter, row) => {
    let filterValue = row[filter.id];

    if (row._original.jobNumbers && row._original.jobNumbers.length) {
        filterValue += ` ${row._original.jobNumbers.join(' ')}`;
    }

    if (row._original.salesforceIds && row._original.salesforceIds.length) {
        filterValue += ` ${row._original.salesforceIds.join(' ')}`;
    }

    return filterValue;
};


const memoizeFilterValue = fastMemoize(generateFilterValue);

class EntitiesTableContainer extends Component {
    constructor(props) {
        super(props);
        this.getColumns = this.getColumns.bind(this);
        this.activeSearch = false;
        this.state = {
            showPagination: false,
            optionsByBrand: {},
            uniqBrandAliases: [],

            currentUrl: props.parsedUrl.href,
            list: null,

            // controlled table
            // filtered: [],
            // sorted: [],
            // page: 0,
            // pageSize: 25
            ...EntitiesTableContainer.convertUriToState(props.parsedUrl)
        };

        this.entityTableInstance = React.createRef();
        this.mirrorStateToUri = debounce(this.mirrorStateToUri.bind(this), 500);
        this.prepareListInfo = memoize(EntitiesTableContainer.prepareListInfo);
        this.setActiveSearch = this.setActiveSearch.bind(this);
    }

    shouldComponentUpdate() {
        return !this.activeSearch;
    }

    setActiveSearch(value) {
        this.activeSearch = value;
    }

    static notAvailableValueFilter(cell) {
        if (!cell) {
            return NOT_AVAILABLE;
        }

        return cell;
    }

    getColumns() {
        return [
            {
                Header: '',
                Cell: props => (
                    <React.Fragment>
                        {this.entityDashboardLink(null, props.original)}
                        {this.linkToEditEntity(null, props.original)}
                        {this.linkToCloneEntity(null, props.original)}
                    </React.Fragment>
                ),
                maxWidth: 80,
                resizable: false,
                sortable: false
            }, {
                Header: 'NAME',
                Cell: props => this.entityNameLink(null, props.original),
                accessor: 'name',
                filterable: true,
                Filter: ({ filter, onChange }) => (
                    <DebouncedInput
                        type="text"
                        className="filter text-filter form-control entityName-filter"
                        onChange={value => onChange(value)}
                        onFocus={() => this.setActiveSearch(true)}
                        onBlur={() => this.setActiveSearch(false)}
                        placeholder="Entity Name, Job Number, or Salesforce ID"
                        style={{ height: 38, borderColor: 'hsl(0,0%,80%)' }}
                        value={filter ? filter.value : ''} />
                ),
                filterMethod: (filter, row) => {
                    const filterValue = memoizeFilterValue(filter, row);

                    return filterValue.toLowerCase().includes(filter.value.toLowerCase());
                }
            }, {
                Header: 'BRAND',
                accessor: 'Brand.name',
                filterable: true,
                maxWidth: 300,
                Filter: ({ filter, onChange }) => {
                    const list = Object.keys(this.state.optionsByBrand);

                    const noBrand = list.indexOf('No Assigned Brand');
                    if (noBrand > 0) {
                        list.splice(noBrand, 1);
                        list.unshift('No Assigned Brand');
                    }

                    return (<Select
                        value={filter && filter.value ? { value: filter.value, label: filter.value } : null}
                        isClearable
                        isSearchable
                        placeholder="Select BRAND..."
                        onChange={event => onChange(event ? event.value : null)}
                        options={list.map(item => ({ value: item, label: item }))}
                    />);
                },
                filterMethod: (filter, row) => {
                    if (!filter.value) {
                        return true;
                    }

                    if (filter.value === 'No Assigned Brand' && !row[filter.id]) {
                        return true;
                    }

                    return filter.value === row[filter.id];
                }
            }, {
                Header: 'LAYER',
                accessor: 'BrandAlias.name',
                filterable: true,
                maxWidth: 300,
                Filter: ({ filter, onChange }) => {
                    const instance = this.getEntityTableInstance().current;
                    const filtered = get(instance, 'state.filtered', []);

                    const currentBrand = get(filtered.find(cur => cur.id === 'Brand.name'), 'value', null);

                    const list = this.state.optionsByBrand[currentBrand] || this.state.uniqBrandAliases;
                    const noBrand = list.indexOf('No Assigned Layer');
                    if (noBrand > 0) {
                        list.splice(noBrand, 1);
                        list.unshift('No Assigned Layer');
                    }

                    return (<Select
                        value={filter && filter.value ? { value: filter.value, label: filter.value } : null}
                        isClearable
                        isSearchable
                        placeholder="Select LAYER..."
                        onChange={event => onChange(event ? event.value : null)}
                        options={list.map(item => ({ value: item, label: item }))}
                    />);
                },
                filterMethod: (filter, row) => {
                    if (!filter.value) {
                        return true;
                    }

                    if (filter.value === 'No Assigned Layer' && !row[filter.id]) {
                        return true;
                    }

                    return filter.value === row[filter.id];
                }
            }, {
                Header: 'ASSOCIATED JOB COUNT',
                accessor: 'jobCount',
                Cell: props => this.jobCountAlt(props.value, props.original),
                maxWidth: 200
            }, {
                Header: 'CREATED',
                accessor: 'createdAt',
                Cell: props => <CellDiv value={this.dateFormatter(props.value)} type="date" style={{ textAlign: 'right' }} />,
                maxWidth: 150
            }
        ];
    }

    componentDidMount() {
        this.props.fetchList();
    }

    static prepareListInfo(list) {
        const optionsByBrand = {};
        const uniqBrandAliases = {};

        for (let i = 0; i < list.length; i += 1) {
            const row = list[i];
            const brandName = get(row, 'Brand.name', 'No Assigned Brand');
            const brandAliasName = get(row, 'BrandAlias.name', 'No Assigned Layer');

            if (!optionsByBrand[brandName]) {
                optionsByBrand[brandName] = [];
            }

            uniqBrandAliases[brandAliasName] = true;

            if (!optionsByBrand[brandName].includes(brandAliasName)) {
                optionsByBrand[brandName].push(brandAliasName);
            }
        }

        return {
            optionsByBrand,
            uniqBrandAliases: Object.keys(uniqBrandAliases)
        };
    }

    static getDerivedStateFromProps(props, state) {
        const propList = get(props, 'entities.list', []);
        const propUrl = get(props, 'parsedUrl', {});
        const mergeList = [];

        if (propList !== state.list) {
            let showPagination = state.showPagination;

            if (!state.showPagination && propList.length > 25) {
                showPagination = true;
            }

            const listInfo = EntitiesTableContainer.prepareListInfo(propList);

            mergeList.push({
                list: propList,
                showPagination,
                ...listInfo
            });
        }

        if (state.currentUrl !== propUrl.href) {
            mergeList.push({
                currentUrl: propUrl.href,
                ...EntitiesTableContainer.convertUriToState(propUrl)
            });
        }

        if (mergeList.length) {
            return Object.assign.apply(null, [
                {},
                state
            ].concat(mergeList));
        }
        return null;
    }

    dateFormatter(cell) {
        return `${moment(cell).format('MM-DD-YYYY')}`;
    }

    linkToEditEntity(cell, row) {
        return (
            <Link to={`/admin/entities/${row.id}/edit`} title="Edit entity" style={{ marginLeft: 15 }}>
                <span className="icon-edit" />
            </Link>
        );
    }

    linkToCloneEntity(cell, row) {
        return (
            <TrackedCloneLink
                to={`/admin/entities/create?templateId=${row.id}`} title="Copy entity"
                style={{ marginLeft: 15 }}
                target="_blank">
                <span className="icon-clone" />
            </TrackedCloneLink>
        );
    }

    jobCountAlt(cell, row) {
        const key = EntitiesTableContainer.isSalesforce(row) ? 'salesforceIds' : 'jobNumbers';
        return <CellDiv value={cell} title={cell > 0 ? row[key].join('\n') : ''} type="number" style={{ textAlign: 'right' }} />;
    }

    entityNameLink(cell, row) {
        return (
            <Link to={`/admin/entities/${row.id}`}>
                {row.name}
            </Link>
        );
    }

    entityDashboardLink(cell, row) {
        return (
            <Link to={`/dashboard/${row.id}/summary`} target="_blank" title="Go to dashboard">
                <span className="icon-open" />
            </Link>
        );
    }

    static isSalesforce(entity) {
        return get(entity, 'entityPlatform', ENTITY_PLATFORMS.default) === ENTITY_PLATFORMS.salesforce;
    }

    getEntityTableInstance() {
        if (!this.entityTableInstance) {
            console.warn('React Table - No instance'); // eslint-disable-line
        }

        return this.entityTableInstance;
    }

    onFilteredChangeCustom(value, accessor) {
        let filtered = this.state.filtered;

        const tempFilters = filtered.reduce((prev, filter) => {
            prev[filter.id] = filter.value;
            return prev;
        }, {});

        tempFilters[accessor] = value;

        // If alias is selected and it's not in the Brand, clear the alias filter
        if (tempFilters['Brand.name']
            && tempFilters['BrandAlias.name']
            && this.state.optionsByBrand[tempFilters['Brand.name']].includes(tempFilters['BrandAlias.name']) === false) {
            delete tempFilters['BrandAlias.name'];
        }

        filtered = Object.keys(tempFilters).map((key) => {
            if (tempFilters[key] === null) {
                return false;
            }
            return { id: key, value: tempFilters[key] };
        }).filter(item => item);

        const instance = this.getEntityTableInstance().current;
        let page = this.state.page;
        if (get(instance, 'state.sortedData', []).length < this.state.page * this.state.pageSize) {
            page = DEFAULT_PAGE;
        }

        this.setState({ filtered, page }, () => this.mirrorStateToUri());
    }

    mirrorStateToUri() {
        const query = {};

        this.state.filtered.forEach((filter) => {
            switch (filter.id) {
            case 'Brand.name':
                query.brandName = filter.value;
                break;

            case 'BrandAlias.name':
                query.brandAlias = filter.value;
                break;

            case 'name':
                if (filter.value) {
                    query.entityName = filter.value;
                }
                break;

            default:
                // ignore
            }
        });

        if (this.state.page !== DEFAULT_PAGE) {
            query.page = this.state.page;
        }

        if (this.state.pageSize !== DEFAULT_PAGE_SIZE) {
            query.pageSize = this.state.pageSize;
        }

        if (this.state.sorted.length) {
            const sortedItem = this.state.sorted[0];

            query.sort = sortedItem.id;
            query.sortDir = sortedItem.desc ? 'desc' : 'asc';
        }

        this.props.parsedUrl.set('query', query);
        this.props.push(this.props.parsedUrl.toString({ onlyPath: true }));
    }

    static convertUriToState(url) {
        const filtered = [];
        const sorted = [];
        const { query } = url;

        if (query.brandName) {
            filtered.push({ id: 'Brand.name', value: query.brandName });
        }

        if (query.brandAlias) {
            filtered.push({ id: 'BrandAlias.name', value: query.brandAlias });
        }

        if (query.entityName) {
            filtered.push({ id: 'name', value: query.entityName });
        }

        if (query.sort) {
            sorted.push({ id: query.sort, desc: query.sortDir === 'desc' });
        }

        let page = Number(query.page);
        if (!page || page < 0) {
            page = DEFAULT_PAGE;
        }

        let pageSize = Number(query.pageSize);
        if (!pageSize || pageSize < 1) {
            pageSize = DEFAULT_PAGE_SIZE;
        }

        return {
            filtered,
            sorted,
            page,
            pageSize
        };
    }

    render() {
        const data = this.props.entities.list.map(row => ({
            ...row,
            jobCount: get(row, 'salesforceIds', []).length + get(row, 'jobNumbers', []).length
        }));

        const dataFetched = this.props.entities.listFetched;

        return (
            <Grid fluid>
                <ButtonStandardPrimary
                    type="submit"
                    onClick={() => this.props.push('/admin/entities/create')}>
                    Create Entity
                </ButtonStandardPrimary>
                <br />
                <br />
                {dataFetched ? (
                    <StyledReactTable
                        data={data}
                        ref={this.entityTableInstance}
                        defaultPageSize={25}
                        minRows={10}
                        filtered={this.state.filtered}
                        sorted={this.state.sorted}
                        page={this.state.page}
                        pageSize={this.state.pageSize}
                        onFilteredChange={(filtered, column, value) => {
                            this.onFilteredChangeCustom(value, column.id || column.accessor);
                        }}
                        onSortedChange={sorted => this.setState({ sorted }, () => this.mirrorStateToUri())}
                        onPageChange={page => this.setState({ page }, () => this.mirrorStateToUri())}
                        onPageSizeChange={(pageSize, page) => this.setState({
                            page, pageSize
                        }, () => this.mirrorStateToUri())}
                        getTheadThProps={() => ({
                            style: {
                                backgroundColor: '#E2E2E2',
                            }
                        })}
                        getTheadFilterThProps={() => ({
                            style: {
                                overflow: 'inherit' // allows react-select to work
                            }
                        })}
                        columns={this.getColumns()}
                        defaultSorted={[{
                            id: 'name', desc: false
                        }]}
                        className="-highlight"
                        showPagination={this.state.showPagination}
                        PaginationComponent={Pagination}
                    />
                ) : (

                    <LoadingTile />
                )}
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    entities: state.entities,
    currentUser: state.users.currentUser,
    parsedUrl: state.router.parsedUrl
});

export default connect(mapStateToProps, { fetchList, push })(EntitiesTableContainer);



// WEBPACK FOOTER //
// ./src/containers/tables/EntitiesTableContainer.js